import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { identityApi } from '../api/identityApi';

interface UserInfo {
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  deliveryAddress?: string;
  newsletter: boolean;
}

export default function AccountInfoPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedInfo, setEditedInfo] = useState<UserInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await identityApi.getUserInfo();
        setUserInfo(response);
        setEditedInfo(response);
      } catch (err) {
        setError('Ошибка при загрузке данных пользователя');
      }
    };

    fetchUserInfo();
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      if (!editedInfo) return;
      
      await identityApi.updateUserInfo({
        firstName: editedInfo.firstName,
        lastName: editedInfo.lastName,
        phoneNumber: editedInfo.phone,
        deliveryAddress: editedInfo.deliveryAddress,
        newsletter: editedInfo.newsletter
      });
      
      setUserInfo(editedInfo);
      setIsEditing(false);
      setError(null);
      setSuccess('Данные успешно обновлены');
    } catch (err) {
      setError('Ошибка при сохранении данных');
    }
  };

  const handleChange = (field: keyof UserInfo, value: string | boolean) => {
    if (!editedInfo) return;
    setEditedInfo({ ...editedInfo, [field]: value });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!userInfo) {
    return <div className="text-gray-300 mt-12">Загружаем информацию...</div>;
  }

  return (
    <article className="flex w-0 flex-1 flex-col gap-4 md:gap-6 py-6 md:py-10 px-4 md:px-0">
      <h1 className="text-xl md:text-2xl font-semibold">Личный кабинет</h1>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <h2 className="text-base md:text-lg font-medium mb-4 md:mb-6">Информация о пользователе</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Левая колонка - основная информация */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Электронная почта</label>
              <div className="px-4 py-2 bg-gray-50 rounded-full text-gray-900">
                {userInfo.email}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Имя</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedInfo?.firstName || ''}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите имя"
                />
              ) : (
                <div className="px-4 py-2 bg-gray-50 rounded-full text-gray-900">
                  {userInfo.firstName || 'Не указано'}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Фамилия</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedInfo?.lastName || ''}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите фамилию"
                />
              ) : (
                <div className="px-4 py-2 bg-gray-50 rounded-full text-gray-900">
                  {userInfo.lastName || 'Не указано'}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Телефон</label>
              {isEditing ? (
                <input
                  type="tel"
                  value={editedInfo?.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Введите телефон"
                />
              ) : (
                <div className="px-4 py-2 bg-gray-50 rounded-full text-gray-900">
                  {userInfo.phone || 'Не указано'}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Адрес доставки</label>
              {isEditing ? (
                <input
                  value={editedInfo?.deliveryAddress || ''}
                  onChange={(e) => handleChange('deliveryAddress', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent placeholder-gray-400 resize-none"
                  placeholder="Введите адрес доставки"
                />
              ) : (
                <div className="px-4 py-2 bg-gray-50 rounded-full text-gray-900">
                  {userInfo.deliveryAddress || 'Не указано'}
                </div>
              )}
            </div>
          </div>

          {/* Правая колонка - дополнительные поля */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Подписка на рассылку</label>
              {isEditing ? (
                <div className="flex items-center space-x-2 px-4 py-2">
                  <input
                    type="checkbox"
                    checked={editedInfo?.newsletter || false}
                    onChange={(e) => handleChange('newsletter', e.target.checked)}
                    className="rounded-full border-gray-300 text-gray-600 focus:ring-gray-500"
                  />
                  <span className="text-sm text-gray-700">Получать уведомления и специальные предложения</span>
                </div>
              ) : (
                <div className="px-4 py-2 bg-gray-50 rounded-full text-gray-900">
                  {userInfo.newsletter ? 'Да' : 'Нет'}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 pl-4">Тип аккаунта</label>
              <div className="px-4 py-2 bg-gray-50 rounded-full text-gray-900">
                Физическое лицо
              </div>
            </div>
          </div>
        </div>

        {/* Кнопки управления */}
        <div className="mt-6 md:mt-8 flex flex-col sm:flex-row justify-end gap-3 md:gap-4">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditedInfo(userInfo);
                  setError(null);
                  setSuccess(null);
                }}
                className="px-4 md:px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleSave}
                className="px-4 md:px-6 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800"
              >
                Сохранить
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleEdit}
                className="px-4 md:px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50"
              >
                Редактировать
              </button>
              <button
                onClick={handleLogout}
                className="px-4 md:px-6 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-full hover:bg-red-50"
              >
                Выйти
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
} 